import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { LOGIN } from "@/constants/testIds";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { isNativeIOS } from "@/lib/nativePush";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null); // {challenge_id, email_hint} when 2FA is required
  const [code, setCode] = useState("");
  const { login, verifyTwoFactor } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  // Native iOS shell: external auth (auth.emergentagent.com) would escape the
  // WKWebView into Safari — email/password only inside the app.
  const nativeIOS = isNativeIOS();
  // Optional post-login redirect (?next=/some/path). Only accept same-origin
  // relative paths so an attacker can't hijack it with an external URL.
  const nextParam = new URLSearchParams(location.search).get("next");
  const safeNext = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//") ? nextParam : null;

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const signInWithGoogle = () => {
    // Explicit "login" intent so a lingering "link" flag from a prior flow
    // doesn't push us onto the wrong callback branch.
    try { window.sessionStorage.removeItem("pandax_google_intent"); } catch (err) { console.debug("session-storage unavailable:", err?.name); }
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error);
      return;
    }
    if (res.require_2fa) {
      setChallenge({ challenge_id: res.challenge_id, email_hint: res.email_hint });
      setCode("");
      toast.info("We just sent a 6-digit code to your email.");
      return;
    }
    toast.success("Welcome back to the vault.");
    nav(safeNext || "/feed");
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^\d{6}$/.test(code.trim())) {
      setErr("Enter the 6-digit code from your email.");
      return;
    }
    setBusy(true);
    const res = await verifyTwoFactor(challenge.challenge_id, code.trim());
    setBusy(false);
    if (!res.ok) {
      setErr(res.error);
      return;
    }
    toast.success("Welcome back to the vault.");
    nav(safeNext || "/feed");
  };

  return (
    <div className="grain min-h-screen flex items-center justify-center px-6 py-12 relative">
      <div className="w-full max-w-md relative z-10">
        <Link to="/" className="flex items-center gap-3 mb-10 justify-center">
          <div className="w-10 h-10 rounded-full bg-rose-600 flex items-center justify-center text-lg">🐼</div>
          <span className="font-display text-2xl tracking-tight">My Panda X</span>
        </Link>
        <div className="card-surface p-8">
          {challenge ? (
            <div data-testid="login-2fa-screen">
              <button
                type="button"
                onClick={() => { setChallenge(null); setCode(""); setErr(""); }}
                className="text-xs text-zinc-500 hover:text-rose-500 flex items-center gap-1 mb-4"
                data-testid="login-2fa-back"
              >
                <ArrowLeft size={12} /> back
              </button>
              <div className="flex items-center gap-2 text-rose-500 mb-1">
                <ShieldCheck size={18} />
                <span className="text-xs uppercase tracking-[0.3em]">Two-factor sign-in</span>
              </div>
              <h1 className="font-display text-3xl mb-1">One more step.</h1>
              <p className="text-sm text-zinc-400 mb-6">
                We sent a 6-digit code to <span className="text-zinc-200">{challenge.email_hint}</span>. It expires in 10 minutes.
              </p>
              <form onSubmit={submitCode} className="space-y-5">
                <Input
                  data-testid="login-2fa-code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  className="text-center text-2xl tracking-[0.6em] bg-black/40 border-white/10 rounded-none py-6 focus-visible:ring-rose-600"
                />
                {err && (
                  <div className="text-sm text-rose-500 border-l-2 border-rose-500 pl-3" data-testid="login-2fa-error">{err}</div>
                )}
                <Button
                  type="submit"
                  disabled={busy || code.length !== 6}
                  className="btn-crimson w-full rounded-none py-6 tracking-wide"
                  data-testid="login-2fa-submit"
                >
                  {busy ? "Verifying…" : "Verify & continue"}
                </Button>
              </form>
            </div>
          ) : (
          <>
          <h1 className="font-display text-3xl mb-1">Welcome back.</h1>
          <p className="text-sm text-zinc-400 mb-6">Log in to enter the vault.</p>

          <Button
            type="button"
            onClick={signInWithGoogle}
            variant="outline"
            className={`w-full rounded-none py-6 bg-transparent border border-white/15 text-white hover:border-rose-600 hover:bg-white/[0.03] items-center justify-center gap-3 ${nativeIOS ? "hidden" : "flex"}`}
            data-testid="login-google-button"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8.1 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 15.2 18.9 12 24 12c3.1 0 5.9 1.2 8.1 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4c-7.7 0-14.3 4.3-17.7 10.7z"/>
              <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.8-5.3l-6.4-5.4C29.5 34.9 26.9 36 24 36c-5.2 0-9.7-3.3-11.3-8l-6.6 5.1C9.5 39.7 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.8l6.4 5.4c-.5.5 6.5-4.8 6.5-15.2 0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            Continue with Google
          </Button>

          {!nativeIOS && (
          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs uppercase tracking-widest text-zinc-500">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          )}

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Email</Label>
              <Input
                data-testid={LOGIN.emailInput}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-black/40 border-white/10 rounded-none py-6 focus-visible:ring-rose-600"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Password</Label>
              <Input
                data-testid={LOGIN.passwordInput}
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-black/40 border-white/10 rounded-none py-6 focus-visible:ring-rose-600"
              />
            </div>
            {err && (
              <div className="text-sm text-rose-500 border-l-2 border-rose-500 pl-3">{err}</div>
            )}
            <Button
              data-testid={LOGIN.submitButton}
              type="submit"
              disabled={busy}
              className="btn-crimson w-full rounded-none py-6 tracking-wide"
            >
              {busy ? "Entering…" : "Enter the Vault →"}
            </Button>
            <div className="text-right">
              <Link
                to="/forgot-password"
                data-testid={LOGIN.forgotPasswordLink}
                className="text-xs text-zinc-500 hover:text-rose-500 transition-colors"
              >
                Forgot your password?
              </Link>
            </div>
          </form>

          <div className="mt-8 text-sm text-zinc-400 text-center">
            No account?{" "}
            <Link
              to="/register"
              data-testid={LOGIN.registerLink}
              className="text-rose-500 hover:underline"
            >
              Create one
            </Link>
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
